

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Handle, Position } from '@xyflow/react';
import { useTranslation } from 'react-i18next';

import type { ArcEvent } from '@/lib/types';

const STATUS_COLORS = {
  complete: { bg: '#10b98115', border: '#10b981', text: '#059669' },
  partial: { bg: '#f59e0b15', border: '#f59e0b', text: '#d97706' },
  notWritten: { bg: '#6b728015', border: '#6b7280', text: '#4b5563' },
};

interface ArcEventNodeProps {
  data: {
    event: ArcEvent;
    status: 'complete' | 'partial' | 'notWritten';
    onDetails: (eventId: string) => void;
  };
  selected?: boolean;
}

const HANDLE_STYLE = {
  width: 10,
  height: 10,
  background: '#94a3b8',
  border: '2px solid white',
  boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
};

export function ArcEventNode({ data, selected }: ArcEventNodeProps) {
  const statusConfig = STATUS_COLORS[data.status] || STATUS_COLORS.notWritten;
  const { t } = useTranslation();

  return (
    <Box>
      <Handle
        type="target"
        position={Position.Left}
        style={{ ...HANDLE_STYLE, left: -5 }}
      />

      <Paper
        elevation={selected ? 6 : 1}
        onClick={() => data.onDetails(data.event.eventId)}
        sx={{
          p: 1.5,
          minWidth: 180,
          maxWidth: 240,
          bgcolor: 'background.paper',
          border: '1.5px solid',
          borderColor: selected ? 'primary.main' : 'divider',
          borderRadius: 2,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          position: 'relative',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 3,
          },
        }}
      >
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {data.event.name}
          </Typography>

          {data.event.summary && (
            <Typography variant="caption" color="text.secondary" sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.2
            }}>
              {data.event.summary}
            </Typography>
          )}

          <Chip
            size="small"
            label={data.status === 'complete' ? t('arcs.status_complete') : data.status === 'partial' ? t('arcs.status_partial') : t('arcs.status_not_written')}
            sx={{
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 600,
              bgcolor: statusConfig.bg,
              color: statusConfig.text,
              border: `1px solid ${statusConfig.border}40`,
              alignSelf: 'flex-start',
              mt: 0.5
            }}
          />
        </Stack>
      </Paper>

      <Handle
        type="source"
        position={Position.Right}
        style={{ ...HANDLE_STYLE, right: -5 }}
      />
    </Box>
  );
}
